import { take, call, put } from 'redux-saga/effects';
import * as types from '../../lib/actionTypes.es6';
import {
  getUsers,
  getAgentUsers,
  createUser,
  getPartners,
  getRoles,
  changeUserPassword,
  updateUserRolesAndPartners,
  recoverPassword,
  getUser,
  deleteUser,
  disableUser,
  enableUser,
  getCurrentRoles,
  getCurrentUserPartners,
  getDisabledUsers,
  getUserAuthActivities,
  getAllUsersAuthActivities
} from '../../lib/requests.es6';
import * as actions from './actions.es6';
import { unAuthorizedNotification, getPartnerGuid } from '../../lib/access.es6';
import { GAloggerException } from '../../lib/monitoring.es6';

function filterUsersByPartner(users) {
  var partnerGuid = getPartnerGuid();
  if (!partnerGuid) return users;
  return users.filter(function(user) {
    var partners = user.attributes && user.attributes.partners;
    if (!partners || !partners.length) return false;
    return partners.indexOf(partnerGuid) !== -1;
  });
}

export function* getUsersRequestWatcher() {
  for (;;) {
    const action = yield take('GET_USERS_REQUEST');
    try {
      const params = action.payload || {};
      const max = params.max || 10;
      // Fetch one extra to detect if there's a next page
      const fetchParams = Object.assign({}, params, { max: max + 1 });
      const response = yield call(getUsers, fetchParams);
      const all = filterUsersByPartner(response.data);
      const hasNextPage = all.length > max;
      const users = hasNextPage ? all.slice(0, max) : all;
      yield put(actions.getUsersSuccess({ users: users, hasNextPage: hasNextPage, page: params.first || 0, max: max }));
    } catch (e) {
      yield put(actions.getUsersFail('An error occurred, this could be due to invalid data '));
      GAloggerException(e);
      unAuthorizedNotification(e);
    }
  }
}

export function* getAgentsRequestWatcher() {
  for (;;) {
    const action = yield take('GET_AGENTS_REQUEST');
    try {
      const params = action.payload || {};
      const response = yield call(getAgentUsers, params);
      const agents = response.data.filter(function(u) {
        return u.attributes && u.attributes.isAgent && u.attributes.isAgent[0] === 'true';
      });
      yield put(actions.getAgentsSuccess(agents));
    } catch (e) {
      yield put(actions.getAgentsFail('Failed to load agents'));
      GAloggerException(e);
      unAuthorizedNotification(e);
    }
  }
}

export function* getUserRequestWatcher() {
  for (;;) {
    try {
      const action = yield take('GET_USER_REQUEST');
      const response = yield call(getUser, action.payload);
      yield put(actions.getUserSuccess(response.data));
    } catch (e) {
      yield put(
        actions.getUserFail(
          'An error occurred, this could be due to invalid data '
        )
      );
      GAloggerException(e);
      unAuthorizedNotification(e);
    }
  }
}

export function* createUserRequestWatcher() {
  for (;;) {
    try {
      const action = yield take('CREATE_USERS_REQUEST');
      const response = yield call(createUser, action.payload);
      yield put(actions.createUsersSuccess(response));
      yield put(actions.getUsersRequest());
    } catch (e) {
      yield put(actions.createUsersFail(e.response.data));
      unAuthorizedNotification(e);
    }
  }
}

export function* getPartnersRequestWatcher() {
  while (yield take('GET_PARTNERS_REQUEST')) {
    try {
      const response = yield call(getPartners);
      yield put(actions.getPartnersSuccess(response.data));
    } catch (e) {
      yield put(
        actions.getPartnersFail(
          'An error occurred, this could be due to invalid data '
        )
      );
      unAuthorizedNotification(e);
    }
  }
}

export function* getRolesRequestWatcher() {
  while (yield take('GET_ROLES_REQUEST')) {
    try {
      const response = yield call(getRoles);
      yield put(actions.getRolesSuccess(response.data));
    } catch (e) {
      yield put(
        actions.getRolesFail(
          'An error occurred, this could be due to invalid data '
        )
      );
      unAuthorizedNotification(e);
    }
  }
}

export function* getCurrentRolesRequestWatcher() {
  for(;;){
   
    try {
      const action = yield take(types.GET_CURRENT_ROLES_REQUEST);
      const payload = {
        id: action.payload,
      }
      const response = yield call(getCurrentRoles, payload);
      yield put(actions.getCurrentRolesSuccess(response.data));
    } catch (e) {
      yield put(
        actions.getCurrentRolesFail(
          'An error occurred, this could be due to invalid data '
        )
      );
      unAuthorizedNotification(e);
    }
  }
}

export function* getCurrentPartnersRequestWatcher() {
  for(;;){
    try {
      const action = yield take(types.GET_CURRENT_PARTNER_REQUEST);
      const payload = {
        id: action.payload,
      }
      const response = yield call(getCurrentUserPartners, payload);
      yield put(actions.getCurrentPartnerSuccess(response.data.attributes.partners));
    } catch (e) {
      yield put(
        actions.getCurrentPartnerFail(
          'An error occurred, this could be due to invalid data '
        )
      );
      unAuthorizedNotification(e);
    }
  }
 
}

export function* deleteUserRequestWatcher() {
  for (;;) {
    try {
      const action = yield take(types.DELETE_USER_REQUEST);
      const payload = {
        id: action.payload,
        enabled: false
      }
      console.log("deleting user", payload)
      yield call(deleteUser, payload);
      yield put(actions.getUsersRequest());
      yield put(actions.deleteUsersSuccess('User Deleted successfully'))
    } catch (e) {
      yield put(
        actions.deleteUsersFail(
          'An error occurred, this could be due to invalid data '
        )
      );
      unAuthorizedNotification(e);
    }
  }
}

export function* changeUserPasswordRequestWatcher() {
  for (;;) {
    try {
      const action = yield take('CHANGE_USER_PASSWORD_REQUEST');
      yield call(changeUserPassword, action.payload);
    } catch (e) {
      unAuthorizedNotification(e);
    }
  }
}

export function* updateRolesAndPartnersRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_ROLES_AND_PARTNERS_REQUEST);
    try {
      const response = yield call(updateUserRolesAndPartners, request.payload);
      yield put(actions.updateRolesAndPartnersdSuccess(response.data));
      yield put(actions.getUsersRequest());
    } catch (e) {
      yield put(actions.updateRolesAndPartnersdFail('Something went wrong'));
      GAloggerException(e);
      unAuthorizedNotification(e);
    }
  }
}

export function* resetPasswordRequestWatcher() {
  while (true) {
    const response = yield take(types.RESET_USER_PASSWORD_REQUEST);
    try {
      yield call(recoverPassword, response.payload);
      yield put(actions.resetPasswordSuccess('password reset success'));
    } catch (e) {
      GAloggerException(e);
      yield put(actions.resetPasswordFailure('Something went wrong'));
    }
  }
}

export function* disableUserRequestWatcher() {
  while (true) {
    const action = yield take(types.DISABLE_USER_REQUEST);
    try {
      const payload = {
        id: action.payload,
        enabled: false
      }
      console.log("disabling user", payload)
      yield call(disableUser, payload);
      yield put(actions.disableUserSuccess('User disabled successfully'));
      yield put(actions.getUsersRequest());
    } catch (e) {
      GAloggerException(e);
      yield put(actions.disableUserFailure('Something went wrong'));
      unAuthorizedNotification(e);
    }
  }
}

export function* enableUserRequestWatcher() {
  while (true) {
    const action = yield take(types.ENABLE_USER_REQUEST);
    try {
      const payload = {
        id: action.payload,
        enabled: true
      }
      console.log("enabling user", payload)
      yield call(enableUser, payload);
      yield put(actions.enableUserSuccess('User enabled successfully'));
      yield put(actions.getUsersRequest());
    } catch (e) {
      GAloggerException(e);
      yield put(actions.enableUserFailure('Something went wrong'));
      unAuthorizedNotification(e);
    }
  }
}

export function* getDisabledUsersRequestWatcher() {
  for (;;) {
    const action = yield take(types.GET_DISABLED_USERS_REQUEST);
    try {
      const params = action.payload || {};
      const max = params.max || 10;
      const fetchParams = Object.assign({}, params, { max: max + 1 });
      const response = yield call(getDisabledUsers, fetchParams);
      const all = filterUsersByPartner(response.data);
      const hasNextPage = all.length > max;
      const users = hasNextPage ? all.slice(0, max) : all;
      yield put(actions.getDisabledUsersSuccess({ users: users, hasNextPage: hasNextPage, page: params.first || 0, max: max }));
    } catch (e) {
      yield put(actions.getDisabledUsersFail('An error occurred, this could be due to invalid data '));
      GAloggerException(e);
      unAuthorizedNotification(e);
    }
  }
}

export function* getUserAuthActivitiesRequestWatcher() {
  for (;;) {
    try {
      const action = yield take(types.GET_USER_AUTH_ACTIVITIES_REQUEST);
      const response = yield call(getUserAuthActivities, action.payload);
      yield put(actions.getUserAuthActivitiesSuccess(response.data));
    } catch (e) {
      yield put(
        actions.getUserAuthActivitiesFail(
          'An error occurred, this could be due to invalid data '
        )
      );
      GAloggerException(e);
      unAuthorizedNotification(e);
    }
  }
}

export function* getAllUsersAuthActivitiesRequestWatcher() {
  for (;;) {
      const request = yield take(types.GET_USERS_AUTH_ACTIVITIES_REQUEST);
      try {
        const response = yield call(getAllUsersAuthActivities, request.payload);
        yield put(actions.getAllUsersAuthActivitiesSuccess(response.data));
      } catch (e) {
        let errorMessage = "An unexpected error occurred";
        if (e.response) {
          errorMessage = e.response.data;
        }
        yield put(actions.getAllUsersAuthActivitiesFail(errorMessage));
  
        GAloggerException(e);
        unAuthorizedNotification(e);
      }
    }
} 
